import React from 'react';
import { Text } from '@charlietango/ui';
import { rem } from '../../styles/rem';
import { colors } from '../../styles/colors';
import { ButtonVariant } from '../../styles/buttons';
import { FontWeight, TextVariant } from '../../styles/typography';
import { StatusBarViewModel } from './types/StatusBarViewModel';
import Button from '../Button/Button';
import { fixedBottom } from '../../styles/fixedBottom';
import { ElectionType } from '../../api/api-types';
import { DeclarationActionTypes } from '../../view-models/DeclarationActionTypes';
import GuardedDialogAction from './GuardedDialogAction';

const compactTabletQuery = '@media (min-width: 40em)';

type StatusActionBarProps = {
  compact?: boolean;
  partyName?: string | null;
  electionLabel: string;
  loginUrl?: string;
  startDeclarationUrl?: string;
  userVotingRights?: {
    dk?: boolean | null;
    eu?: boolean | null;
  };
  isLoggedIn: boolean;
  canStartDeclaration: boolean;
  shouldRender?: boolean;
  statusBarModel?: StatusBarViewModel;
  startDeclarationTargets?: {
    action?: DeclarationActionTypes;
    electionType?: ElectionType;
    partyId?: string;
  }[];
};

function RightsWarnings({
  userVotingRights,
  statusBarModel,
}: Pick<StatusActionBarProps, 'userVotingRights' | 'statusBarModel'>) {
  if (!userVotingRights) return null;

  const warningStyles = {
    color: colors.base.Text.Brand,
    fontWeight: FontWeight.Medium,
  };

  if (!userVotingRights.dk && userVotingRights.eu) {
    return (
      <Text as="p" variant={TextVariant.BodyMd} sx={warningStyles}>
        {statusBarModel?.noDkRightsText ??
          'Du har ikke valgret til Folketingsvalg'}
      </Text>
    );
  }

  if (!userVotingRights.eu && userVotingRights.dk) {
    return (
      <Text as="p" variant={TextVariant.BodyMd} sx={warningStyles}>
        {statusBarModel?.noEuRightsText ??
          'Du har ikke valgret til Europa-Parlamentsvalg'}
      </Text>
    );
  }

  if (!userVotingRights.dk && !userVotingRights.eu) {
    return (
      <Text as="p" variant={TextVariant.BodyMd} sx={warningStyles}>
        {statusBarModel?.noBothRightsText ??
          'Du har ikke valgret til Folketingsvalg eller Europa-Parlamentsvalg'}
      </Text>
    );
  }

  return null;
}

function StatusActionBar({
  compact = false,
  partyName,
  electionLabel,
  loginUrl,
  startDeclarationUrl,
  userVotingRights,
  isLoggedIn,
  canStartDeclaration,
  shouldRender = true,
  statusBarModel,
  startDeclarationTargets,
}: StatusActionBarProps) {
  if (!shouldRender) return null;

  const shouldRenderRightsWarning =
    isLoggedIn &&
    !!userVotingRights &&
    (!userVotingRights.dk || !userVotingRights.eu);

  return (
    <div
      sx={{
        minHeight: compact ? rem(60) : [0, rem(88)],
        backgroundColor: fixedBottom.actionBarBackground,
        color: colors.base.Text.Brand,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <div
        sx={{
          mx: 'auto',
          width: '100%',
          maxWidth: rem(1032),
          px: [rem(20), rem(20), rem(40)],
          py: compact ? rem(12) : undefined,
          pt: compact ? undefined : [rem(20), 0],
          pb: compact ? undefined : [rem(28), 0],
          display: 'flex',
          flexDirection: compact ? 'row' : ['column', 'row'],
          justifyContent: 'space-between',
          alignItems: compact ? 'center' : ['stretch', 'center'],
          gap: compact ? rem(16) : [rem(20), rem(20), rem(28)],
          ...(compact
            ? undefined
            : {
                [compactTabletQuery]: {
                  flexDirection: 'row',
                  alignItems: 'center',
                },
              }),
        }}
      >
        <div
          sx={{
            minWidth: 0,
            flex: compact ? '1 1 auto' : undefined,
            overflow: compact ? 'hidden' : undefined,
          }}
        >
          <Text
            as="p"
            variant={compact ? TextVariant.HeaderXs : TextVariant.HeaderSm}
            sx={{
              color: colors.base.Text.Brand,
              display: compact ? 'block' : undefined,
              fontSize: compact ? [rem(14), rem(16)] : undefined,
              fontWeight: FontWeight.Medium,
              alignItems: 'baseline',
              lineHeight: compact ? [rem(20), rem(24)] : undefined,
              overflow: compact ? 'hidden' : undefined,
              textOverflow: compact ? 'ellipsis' : undefined,
              whiteSpace: compact ? 'nowrap' : undefined,
              ...(compact
                ? undefined
                : {
                    [compactTabletQuery]: {
                      display: 'flex',
                      minWidth: 0,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                    },
                  }),
            }}
          >
            <span
              sx={{
                ...(compact
                  ? undefined
                  : {
                      [compactTabletQuery]: {
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      },
                    }),
              }}
            >
              {partyName ?? ''}
            </span>
            <span
              aria-hidden="true"
              sx={{
                display: compact ? ['none', 'inline'] : 'inline',
                mx: rem(4),
              }}
            >
              /
            </span>
            <span
              sx={{
                display: compact ? ['none', 'inline'] : 'inline',
                flexShrink: compact ? undefined : 0,
                fontFamily: 'body',
                fontWeight: FontWeight.Normal,
              }}
            >
              {electionLabel}
            </span>
          </Text>
          {shouldRenderRightsWarning && (
            <div sx={{ mt: [rem(8)] }}>
              <RightsWarnings
                userVotingRights={userVotingRights}
                statusBarModel={statusBarModel}
              />
            </div>
          )}
        </div>

        <div
          sx={{
            display: 'flex',
            flexDirection: compact ? 'row' : ['column', 'row'],
            alignItems: compact ? 'center' : ['stretch', 'center'],
            justifyContent: 'flex-end',
            gap: compact ? undefined : [rem(12), rem(12), rem(12)],
            flexShrink: 0,
            minWidth: compact ? 'fit-content' : undefined,
            ...(compact
              ? undefined
              : {
                  [compactTabletQuery]: {
                    flexDirection: 'row',
                    alignItems: 'center',
                  },
                }),
          }}
        >
          {!compact && !isLoggedIn && (
            <Button
              variant={ButtonVariant.Link}
              href={loginUrl}
              sx={{
                order: [2, 1],
                backgroundColor: [
                  colors.components.button.Tertiary.Background.Default,
                  'transparent',
                ],
                borderRadius: rem(80),
                color: [
                  colors.components.button.Tertiary.Text.Default,
                  colors.components.button.Ghost.Text.Default,
                ],
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: [rem(44), rem(48)],
                fontWeight: FontWeight.Medium,
                lineHeight: rem(16),
                px: [rem(28), rem(32)],
                py: [rem(14), rem(16)],
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                width: 'auto',
                '@media (max-width: 39.99em)': {
                  width: '100%',
                },
                '&:hover': {
                  backgroundColor: [
                    colors.components.fixedTop.Background.Default,
                    'transparent',
                  ],
                  color: [
                    colors.components.button.Tertiary.Text.Hover,
                    colors.components.button.Ghost.Text.Hover,
                  ],
                },
              }}
            >
              {statusBarModel?.loginLabel ?? 'Log ind'}
            </Button>
          )}
          {canStartDeclaration && (
            <GuardedDialogAction
              action={DeclarationActionTypes.initiate}
              href={startDeclarationUrl}
              targets={startDeclarationTargets ?? []}
            >
              {({ onClick }) => (
                <Button
                  variant={ButtonVariant.Primary}
                  href={startDeclarationUrl}
                  onClick={onClick}
                  sx={{
                    order: compact ? undefined : [1, 2],
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: compact ? rem(36) : rem(44),
                    fontSize: compact ? rem(14) : undefined,
                    lineHeight: compact ? rem(14) : rem(16),
                    minWidth: 0,
                    width: 'auto',
                    '@media (max-width: 39.99em)': {
                      width: compact ? 'auto' : '100%',
                    },
                    px: compact ? rem(20) : [rem(28), rem(32)],
                    py: compact ? rem(11) : rem(14),
                    whiteSpace: 'nowrap',
                  }}
                >
                  {statusBarModel?.startDeclarationLabel ??
                    'Start vælgererklæring'}
                </Button>
              )}
            </GuardedDialogAction>
          )}
        </div>
      </div>
    </div>
  );
}

export default StatusActionBar;
